/**
 * The closed telemetry vocabulary: every enum and every attribute key, in one place.
 *
 * WHY THIS FILE. docs/telemetry.md §25 Phase 1 asks for "the remaining closed enums from §5 as
 * exported TypeScript unions in one shared location, so a typo is a compile error". It did not say
 * where, and the obvious candidate - queue-contracts/src/index.ts - cannot host them: that file is
 * copied verbatim and alone into scanner-runtime/generated/ and shipped as raw TS in the supervisor
 * image, so it must stay self-contained.
 *
 * Each enum is a `const` array AND a derived union. The array matters as much as the type: §16.6
 * requires validating `journey.name` from untrusted baggage against the closed set at RUNTIME, and
 * a type alone cannot do that.
 *
 * The cardinality class in each comment is from §17 and decides one thing: whether the value may be
 * a metric dimension. "low" may; "high" may not, ever.
 */

/** Helper: freeze a list and derive its union, so the values are usable at runtime and as a type. */
const closed = <const T extends readonly string[]>(values: T): T => values

// ─── Service identity (§4) ───────────────────────────────────────────────────────────────────────

/**
 * The fixed `service.name` values. Low cardinality (12). Do not invent more: this is the one
 * attribute that separates signals per component, and the gateway's public receiver allowlists the
 * two browser names by hand.
 */
export const SERVICE_NAMES = closed([
  'terramantle-web',
  'terramantle-app',
  'terramantle-worker',
  'terramantle-module-scanner',
  'terramantle-state-scanner',
  'terramantle-plan-scanner',
  'terramantle-provider-scanner',
  'terramantle-provider-builder',
  'terramantle-provider-gate-runner',
  'terramantle-provider-hasher',
  'terramantle-provider-signer',
  'terramantle-queue-supervisor',
])
export type ServiceName = (typeof SERVICE_NAMES)[number]

/** The two browser packages. The gateway drops anything else arriving on the public receiver. */
export const BROWSER_SERVICE_NAMES = closed(['terramantle-web', 'terramantle-app'])

/** `deployment.environment.name`. Low cardinality (4). */
export const ENVIRONMENTS = closed(['production', 'staging', 'development', 'e2e'])
export type Environment = (typeof ENVIRONMENTS)[number]

// ─── Journeys (§7) ───────────────────────────────────────────────────────────────────────────────

/**
 * The nine journeys. Low cardinality (9), and the value that makes the high-cardinality
 * `journey.id` filterable without knowing the id.
 */
export const JOURNEY_NAMES = closed([
  'authentication',
  'onboarding',
  'module.publish',
  'state.apply',
  'plan.govern',
  'provider.build',
  'provider.scan',
  'policy.author',
  'marketing.acquisition',
])
export type JourneyName = (typeof JOURNEY_NAMES)[number]

/**
 * The subset the Worker can attribute at `Queue.send` time.
 *
 * Narrower than JourneyName on purpose: the other five journeys either belong to the browser or
 * have no queue hand-off, so letting an enqueue claim them would produce a journey id that points
 * at no durable row.
 */
export const QUEUE_JOURNEY_NAMES = closed([
  'module.publish',
  'plan.govern',
  'provider.build',
  'provider.scan',
  'state.apply',
])
export type QueueJourneyName = (typeof QUEUE_JOURNEY_NAMES)[number]

// ─── Outcomes and gates (§5, §13) ────────────────────────────────────────────────────────────────

/**
 * `tm.outcome` - the BUSINESS result, which is not the same thing as span status. Low cardinality
 * (10).
 *
 * This is the attribute that distinguishes "the product worked and said no" from "we broke". A
 * blocked plan is `blocked` with span status OK; only `failed` implies something went wrong.
 */
export const TM_OUTCOMES = closed([
  'ok',
  'blocked',
  'denied',
  'quota_exceeded',
  'deduped',
  'stale',
  'not_found',
  'conflict',
  'too_large',
  'failed',
  // Not in §5's list, but the supervisor already emits it for an Argo node that was never run.
  'skipped',
  'in_progress',
])
export type TmOutcome = (typeof TM_OUTCOMES)[number]

/** The outcomes that must always survive sampling (§18 rule 1, enforced by the gateway tail sampler). */
export const ALWAYS_SAMPLED_OUTCOMES = closed(['blocked', 'denied', 'quota_exceeded', 'failed'])

/** `tm.gate.source`. Low cardinality (2). Matches `plan_policy_results.source`. */
export const GATE_SOURCES = closed(['builtin', 'opa'])
export type GateSource = (typeof GATE_SOURCES)[number]

/** `tm.gate.verdict`. Low cardinality (3). `hold` is `require_approval`. */
export const GATE_VERDICTS = closed(['pass', 'block', 'hold'])
export type GateVerdict = (typeof GATE_VERDICTS)[number]

/**
 * `tm.gate.reason`. Low cardinality (6).
 *
 * An enum EXISTS so that `failure_msg` never has to be emitted: that column is free text produced
 * by customer Rego and can interpolate values out of the policy input (§16).
 */
export const GATE_REASONS = closed([
  'max_destroys',
  'destroy_denylist',
  'require_approval',
  'opa_deny',
  'engine_unconfigured',
  'engine_unreachable',
])
export type GateReason = (typeof GATE_REASONS)[number]

/** The provider-build gates. Low cardinality (4). */
export const BUILD_GATE_IDS = closed(['g1', 'g2', 'g3', 'g4'])
export type BuildGateId = (typeof BUILD_GATE_IDS)[number]

// ─── Artefacts and tools (§5) ────────────────────────────────────────────────────────────────────

/** `tm.artefact.kind` - the core domain axis. Low cardinality (4). */
export const ARTEFACT_KINDS = closed(['module', 'state', 'plan', 'provider'])
export type ArtefactKind = (typeof ARTEFACT_KINDS)[number]

/** `tm.tool.name` - the `ScanTool` union. Low cardinality (7). */
export const TOOL_NAMES = closed(['trivy', 'kics', 'tflint', 'trufflehog', 'checkov', 'clamav', 'semgrep'])
export type ToolName = (typeof TOOL_NAMES)[number]

/**
 * `tm.tool.result` - how a tool run ended. Low cardinality (3).
 *
 * The one dimension that distinguishes "this tool found nothing" from "this tool never ran".
 * runTool degrades a failed or timed-out tool to its empty fallback, so a scan with a dead trivy
 * reports zero findings and every severity policy passes it. That is the silent-under-detection
 * failure mode, and without this label the duration histogram cannot even count how often it
 * happens.
 */
export const TOOL_RESULTS = closed(['ok', 'fail', 'timeout'])
export type ToolResultDim = (typeof TOOL_RESULTS)[number]

/** `tm.provider.upstream` - the lineage discriminator. Low cardinality (3). */
export const PROVIDER_UPSTREAMS = closed(['terraform.io', 'opentofu.org', 'self_built'])
export type ProviderUpstream = (typeof PROVIDER_UPSTREAMS)[number]

/** `tm.scan.mode` - the wire contract's `ProviderScanJobMessage.mode`. Low cardinality (2). */
export const SCAN_MODES = closed(['mirror', 'build_publish'])
export type ScanMode = (typeof SCAN_MODES)[number]

/** `tm.findings.severity` on a per-finding log record. Matches the findings tables. Low (5). */
export const FINDING_SEVERITIES = closed(['critical', 'high', 'medium', 'low', 'info'])
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number]

/** `tm.findings.category` on a per-finding log record. Matches the findings tables. Low (5). */
export const FINDING_CATEGORIES = closed(['secret', 'misconfiguration', 'malware', 'exposure', 'vulnerability'])
export type FindingCategory = (typeof FINDING_CATEGORIES)[number]

/** `tm.serve.decision`. Low cardinality (7). */
export const SERVE_DECISIONS = closed([
  'serve',
  'block',
  'off',
  'substituted',
  'yanked',
  'blocklisted',
  'policy_blocked',
])
export type ServeDecision = (typeof SERVE_DECISIONS)[number]

/**
 * `tm.serve.deny_reason` - WHY a registry serve request was refused (§13).
 *
 * The registry protocol flattens every refusal into a bare 402/403/404, and before this enum the
 * branches were indistinguishable in telemetry: a blocklisted provider, a yanked version, an
 * unentitled org and a cache miss all looked like the same 404. One reason per deny branch is what
 * makes "why are clients getting errors" answerable from a span or a Loki line instead of from the
 * response body nobody kept. Low cardinality (15).
 */
export const SERVE_DENY_REASONS = closed([
  'blocklisted',
  'serve_blocked',
  'version_blocked',
  'policy_blocked',
  // The ORG-WIDE kill switch, distinct from policy_blocked: that one means a verdict about this
  // artefact, this one means the owner refused the whole artefact type (migration 129).
  'consumption_blocked',
  'yanked',
  'not_entitled',
  'not_allowlisted',
  'lineage_missing',
  'not_cached',
  'invalid_filename',
  'org_unresolved',
  'quota_exceeded',
  'signature_invalid',
  'upstream_error',
])
export type ServeDenyReason = (typeof SERVE_DENY_REASONS)[number]

/**
 * `tm.client.name` - the registry client product parsed from the User-Agent (§6).
 *
 * `user_agent.original` already carries the raw string, but raw UAs cannot be grouped on: the
 * version, the provider suffix and the comment URL all vary. This is the low-cardinality twin,
 * and the whole point of it is the Terraform-vs-OpenTofu split. Anything that is neither
 * (curl, go-http-client, a browser, CI probes) is `other`, never absent - absence would make the
 * split's denominator wrong. Low cardinality (3), metric-safe.
 */
export const TM_CLIENT_NAMES = closed(['terraform', 'opentofu', 'other'])
export type TmClientName = (typeof TM_CLIENT_NAMES)[number]

// ─── Actors and roles (§5) ───────────────────────────────────────────────────────────────────────

/**
 * `tm.actor.type`. Low cardinality (5), and §5 calls it "the single most useful low-cardinality
 * dimension this application has" - it separates humans from CI from cron.
 *
 * THREE taxonomies existed before this: `requestLogger` emitted human|bot|oidc, `deriveActor`
 * emitted human|bot_token|vcs, and §5 specified this list. Left alone, `tm.actor.type` would have
 * had seven values and been useless as a metric label. `normaliseActorType` below is the single
 * mapping onto this set.
 */
export const ACTOR_TYPES = closed(['human', 'bot', 'oidc', 'vcs', 'system'])
export type ActorType = (typeof ACTOR_TYPES)[number]

/**
 * Map either of the two legacy taxonomies onto `ActorType`.
 *
 * `vcs` and `oidc` are kept DISTINCT rather than collapsed: a VCS-trusted push and a generic OIDC
 * machine client are different trust paths, and telling them apart is most of the value of the
 * attribute. An unrecognised value becomes `system` rather than throwing - telemetry never breaks
 * the caller.
 */
export function normaliseActorType(raw: string | null | undefined): ActorType {
  switch (raw) {
    case 'human':
      return 'human'
    case 'bot':
    case 'bot_token':
      return 'bot'
    case 'oidc':
      return 'oidc'
    case 'vcs':
      return 'vcs'
    default:
      return 'system'
  }
}

/** `enduser.role` - the `Role` union set by `requireTenantRole`. Low cardinality (4). */
export const ENDUSER_ROLES = closed(['owner', 'admin', 'member', 'reader'])
export type EnduserRole = (typeof ENDUSER_ROLES)[number]

/** `device.class` - the existing viewport+touch bucket. Deliberately not UA-derived. Low (3). */
export const DEVICE_CLASSES = closed(['mobile', 'tablet', 'desktop'])
export type DeviceClass = (typeof DEVICE_CLASSES)[number]

// ─── Queues and workflows (§5) ───────────────────────────────────────────────────────────────────

/** `tm.queue.name`. Low cardinality (7). */
export const QUEUE_NAMES = closed([
  'module-scan',
  'state-scan',
  'plan-scan',
  'provider-scan',
  'provider-build',
  'provider-lookup',
  'policy-bundle-rebuild',
])
export type QueueName = (typeof QUEUE_NAMES)[number]

/** `tm.queue.settle`. Low cardinality (2). */
export const QUEUE_SETTLES = closed(['ack', 'nack'])
export type QueueSettle = (typeof QUEUE_SETTLES)[number]

/**
 * `tm.queue.settle_reason`. Low cardinality (7).
 *
 * The settle decision is the queue's half of the story and nothing else records it - in particular
 * `workflow_absent` above zero is the only signal that events are being lost between the stream and
 * the Sensor, which is the failure ack-on-hand-off could not see.
 */
export const QUEUE_SETTLE_REASONS = closed([
  'succeeded',
  'workflow_failed',
  'workflow_absent',
  'workflow_gone_after_running',
  'pending_cap_exceeded',
  'awaiting_submit',
  'in_progress',
])
export type QueueSettleReason = (typeof QUEUE_SETTLE_REASONS)[number]

/** `tm.workflow.phase` - verbatim from Argo. Low cardinality (5). */
export const WORKFLOW_PHASES = closed(['Succeeded', 'Failed', 'Error', 'Running', 'Pending'])
export type WorkflowPhase = (typeof WORKFLOW_PHASES)[number]

/**
 * `tm.workflow.step` - the DAG task name. Low cardinality (~8).
 *
 * The SAME string a pod receives as TM_STEP_NAME, which is what makes the supervisor and the pod
 * derive the same node span id. Never the pod name: Argo's pod naming has changed between versions
 * and the task name has not.
 */
export const WORKFLOW_STEPS = closed([
  'patch',
  'pin',
  'compile',
  'g124',
  'g3',
  'finalize',
  'scan',
  'report-failure',
])
export type WorkflowStep = (typeof WORKFLOW_STEPS)[number]

/** `tm.build.step` - the build journey's own step enum, a subset of WORKFLOW_STEPS. Low (6). */
export const BUILD_STEPS = closed(['patch', 'pin', 'compile', 'g124', 'g3', 'finalize'])
export type BuildStep = (typeof BUILD_STEPS)[number]

/**
 * `tm.build.patch_mode`. Low cardinality (2).
 *
 * The wire contract spells this as an OPTIONAL `patch_mode?: 'cve_deps'`, where absent means a plain
 * build of an upstream base - deliberately, so an older consumer handles a plain build unchanged.
 * Absent is useless as a metric dimension though (it silently merges into the unlabelled series), so
 * telemetry names it `plain`. That is the one place these two vocabularies differ, and it is why this
 * enum is not simply derived from the contract type.
 */
export const BUILD_PATCH_MODES = closed(['cve_deps', 'plain'])
export type BuildPatchMode = (typeof BUILD_PATCH_MODES)[number]

/** The wire contract's optional `patch_mode` as a metric dimension. */
export function patchModeOf(patchMode: string | undefined): BuildPatchMode {
  return patchMode === 'cve_deps' ? 'cve_deps' : 'plain'
}

// ─── Cron (§5) ───────────────────────────────────────────────────────────────────────────────────

/**
 * `tm.cron.task` - the existing `cron.task` log `name` values. Low cardinality (12).
 *
 * Eight of these share ONE 15-minute cron invocation and therefore one 30s CPU budget, and a single runaway
 * task has previously killed the isolate and every sibling. That is what `tm.cron.task.duration`
 * exists to detect, and why this must stay a closed set.
 */
export const CRON_TASKS = closed([
  'provider_artefact_populate',
  'provider_scan_drain',
  'policy_rescan',
  'prune_download_events',
  'provider_scan_reaper',
  'provider_scan_reconcile',
  'version_scan_reaper',
  'provider_build_reaper',
  'vcs_stats',
  'auto_patch',
  'policy_bundle_reconcile',
  'demo_usage',
])
export type CronTask = (typeof CRON_TASKS)[number]

/** The three cron expressions, for `faas.cron`. */
export const CRON_EXPRESSIONS = closed(['*/15 * * * *', '2-59/5 * * * *', '0 * * * *'])
export type CronExpression = (typeof CRON_EXPRESSIONS)[number]

// ─── Onboarding (§8) ─────────────────────────────────────────────────────────────────────────────

/** `tm.onboarding.step` - the `CreateOrgWizard` steps. Low cardinality (4). */
export const ONBOARDING_STEPS = closed(['create_org', 'policy_config', 'claim_domain', 'vcs_trust'])
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/** The five existing `reportBootstrapError` stages (§8). Low cardinality (5). */
export const BOOTSTRAP_STAGES = closed([
  'signin-redirect',
  'signin-callback',
  'signin-loop',
  'auth-me-unauthorized',
  'auth-me-unavailable',
])
export type BootstrapStage = (typeof BOOTSTRAP_STAGES)[number]

// ─── Attribute keys ──────────────────────────────────────────────────────────────────────────────

/**
 * Every attribute key, so no call site writes a string literal.
 *
 * `tm.*` is the Terramantle-private namespace; everything else is an OpenTelemetry semantic
 * convention and must not be redefined (§6). The OTel ones are listed here rather than imported
 * from `@opentelemetry/semantic-conventions` so this file stays dependency-free and usable in the
 * supervisor's raw-TS image.
 *
 * Deliberately ABSENT: `cloud.region` (a Worker isolate's colo is request-scoped, not
 * resource-scoped), `browser.brands`/`browser.platform` (fingerprinting surface, §4), and anything
 * the gateway upserts - `client.address`, `geo.*`, `cloudflare.ray_id`, `user_agent.original` - which
 * must never be set in application code because `upsert` means the edge value wins anyway.
 */
export const ATTR = {
  // Resource (§4)
  SERVICE_NAME: 'service.name',
  SERVICE_VERSION: 'service.version',
  SERVICE_NAMESPACE: 'service.namespace',
  SERVICE_INSTANCE_ID: 'service.instance.id',
  DEPLOYMENT_ENVIRONMENT: 'deployment.environment.name',
  K8S_NAMESPACE: 'k8s.namespace.name',
  K8S_POD_NAME: 'k8s.pod.name',
  CLOUD_PROVIDER: 'cloud.provider',
  PROCESS_RUNTIME_NAME: 'process.runtime.name',
  PROCESS_RUNTIME_VERSION: 'process.runtime.version',

  // Identity (§3, §5)
  JOURNEY_ID: 'journey.id',
  JOURNEY_NAME: 'journey.name',
  TENANT_ID: 'tenant.id',
  TENANT_SLUG: 'tenant.slug',
  USER_ID: 'user.id',
  /**
   * §16's "User email and name - Never" rule was REMOVED deliberately: `deriveActor` returns
   * `name ?? email ?? sub` for a human and that is now permitted. The trade-off is recorded in
   * §16 - emails live in Tempo for 14d and Loki for 30d, are readable by anyone who can sign into
   * Grafana, and neither store supports per-subject deletion.
   *
   * Server-side only. The browser is not given this: its OTLP endpoint is public and the attribute
   * buys nothing there.
   */
  USER_EMAIL: 'user.email',
  SESSION_ID: 'session.id',
  DEVICE_ID: 'device.id',
  DEVICE_CLASS: 'device.class',
  ENDUSER_ROLE: 'enduser.role',

  // HTTP (§6)
  HTTP_REQUEST_METHOD: 'http.request.method',
  HTTP_ROUTE: 'http.route',
  HTTP_RESPONSE_STATUS_CODE: 'http.response.status_code',
  URL_PATH: 'url.path',
  URL_SCHEME: 'url.scheme',
  URL_FULL: 'url.full',
  SERVER_ADDRESS: 'server.address',
  NETWORK_PROTOCOL_VERSION: 'network.protocol.version',
  USER_AGENT_ORIGINAL: 'user_agent.original',

  // Messaging (§6)
  MESSAGING_SYSTEM: 'messaging.system',
  MESSAGING_OPERATION_NAME: 'messaging.operation.name',
  MESSAGING_DESTINATION_NAME: 'messaging.destination.name',
  MESSAGING_MESSAGE_ID: 'messaging.message.id',

  // Database (§6) - attributes on the enclosing business span, never a span per query
  DB_SYSTEM_NAME: 'db.system.name',
  DB_OPERATION_NAME: 'db.operation.name',
  DB_COLLECTION_NAME: 'db.collection.name',

  // FaaS (§6)
  FAAS_TRIGGER: 'faas.trigger',
  FAAS_CRON: 'faas.cron',

  // GenAI (§6) - NEVER gen_ai.input.messages / .output.messages: the prompt is provider source code
  GEN_AI_OPERATION_NAME: 'gen_ai.operation.name',
  GEN_AI_PROVIDER_NAME: 'gen_ai.provider.name',
  GEN_AI_REQUEST_MODEL: 'gen_ai.request.model',
  GEN_AI_USAGE_INPUT_TOKENS: 'gen_ai.usage.input_tokens',
  GEN_AI_USAGE_OUTPUT_TOKENS: 'gen_ai.usage.output_tokens',

  // Exceptions (§13)
  EXCEPTION_TYPE: 'exception.type',
  EXCEPTION_MESSAGE: 'exception.message',
  EXCEPTION_STACKTRACE: 'exception.stacktrace',

  // Browser (§5)
  APP_PATH: 'app.path',
  APP_FROM: 'app.from',
  /**
   * The route TEMPLATE the SPA matched, e.g. `/org/:orgSlug/modules/:name/:provider/:version?`.
   *
   * `app.path` is the real pathname and is therefore effectively unbounded in the portal: every org
   * slug, module name, workspace key and version id appears in it, so "which pages do people use"
   * cannot be answered by grouping on it. This is the low-cardinality twin that can be grouped on,
   * and it is the browser's counterpart to `http.route` on the server span.
   *
   * Derived from the react-router config, never from `location`, so it cannot carry a query string
   * or a customer-authored segment. Absent when no route matched.
   */
  APP_ROUTE: 'app.route',
  EVENT_NAME: 'event.name',
  EVENT_TYPE: 'event.type',
  EVENT_TARGET: 'event.target',

  // tm.* - actors
  TM_ACTOR_TYPE: 'tm.actor.type',
  TM_ACTOR_NAME: 'tm.actor.name',
  TM_CAMPAIGN: 'tm.campaign',
  TM_OUTCOME: 'tm.outcome',
  TM_SIZE_BYTES: 'tm.size_bytes',
  TM_ERROR_STAGE: 'tm.error.stage',

  // tm.* - artefacts
  TM_ARTEFACT_KIND: 'tm.artefact.kind',
  TM_ARTEFACT_VERSION_ID: 'tm.artefact.version_id',
  /** Normalized display identity across kinds (provider: ns/type; module: ns/name/provider;
   *  state/plan: workspace name). Log-bag attr AND tm.scan.duration dimension - it exists so
   *  Grafana variables can enumerate artefacts via label_values(); cardinality is the artefact
   *  count, bounded. Unlike tenant/user keys it is deliberately NOT a forbidden dimension. */
  TM_ARTEFACT_NAME: 'tm.artefact.name',
  /** The scanned version in display form (provider/module: version string; state/plan: serial).
   *  Same variable-enumeration purpose; cardinality = versions scanned inside metric retention. */
  TM_ARTEFACT_VERSION: 'tm.artefact.version',
  TM_WORKSPACE_ID: 'tm.workspace.id',
  TM_WORKSPACE_NAME: 'tm.workspace.name',
  TM_MODULE_NAMESPACE: 'tm.module.namespace',
  TM_MODULE_NAME: 'tm.module.name',
  TM_MODULE_PROVIDER: 'tm.module.provider',
  /** The module's semver. High cardinality - never a metric dimension. */
  TM_MODULE_VERSION: 'tm.module.version',
  TM_PROVIDER_NAMESPACE: 'tm.provider.namespace',
  TM_PROVIDER_TYPE: 'tm.provider.type',
  TM_PROVIDER_UPSTREAM: 'tm.provider.upstream',
  TM_PROVIDER_VERSION: 'tm.provider.version',
  /** `state_versions.serial` / `.lineage`. Both high cardinality - never metric dimensions. */
  TM_STATE_SERIAL: 'tm.state.serial',
  TM_STATE_LINEAGE: 'tm.state.lineage',
  TM_STORAGE_KEY: 'tm.storage.key',

  // tm.* - gates and policy
  TM_GATE_SOURCE: 'tm.gate.source',
  TM_GATE_VERDICT: 'tm.gate.verdict',
  TM_GATE_REASON: 'tm.gate.reason',
  TM_GATE_ID: 'tm.gate.id',
  TM_POLICY_BUNDLE_REVISION: 'tm.policy.bundle_revision',
  TM_POLICY_COUNT: 'tm.policy.count',
  /** Pass/fail split of one artefact's policy evaluation, on the scan-summary signal. */
  TM_POLICY_PASS_COUNT: 'tm.policy.pass_count',
  TM_POLICY_FAIL_COUNT: 'tm.policy.fail_count',

  // tm.* - jobs
  TM_SCAN_JOB_ID: 'tm.scan.job_id',
  /** `ProviderScanJobMessage.mode` - see SCAN_MODES. */
  TM_SCAN_MODE: 'tm.scan.mode',
  /** The scan rule row id. High cardinality - never a metric dimension. */
  TM_SCAN_RULE_ID: 'tm.scan.rule_id',
  TM_SCAN_MALWARE_DETECTED: 'tm.scan.malware_detected',
  // Drain shape, on the one `provider.scan.drain` span per tick. Counts, not dimensions.
  TM_SCAN_TIER_NEW: 'tm.scan.tier_new',
  TM_SCAN_TIER_RETRY: 'tm.scan.tier_retry',
  TM_SCAN_TIER_RESCAN: 'tm.scan.tier_rescan',
  TM_SCAN_REMAINING: 'tm.scan.remaining',
  TM_SCAN_PATCHED: 'tm.scan.patched',
  /** Findings in one scan or one report. The size signal that explains a slow ingest or scan. */
  TM_FINDINGS_COUNT: 'tm.findings.count',
  // Severity split of TM_FINDINGS_COUNT, on scan spans and the scan-summary log record.
  TM_FINDINGS_CRITICAL: 'tm.findings.critical',
  TM_FINDINGS_HIGH: 'tm.findings.high',
  TM_FINDINGS_MEDIUM: 'tm.findings.medium',
  TM_FINDINGS_LOW: 'tm.findings.low',
  TM_FINDINGS_INFO: 'tm.findings.info',
  // One finding on a per-finding log record (§14). Rule id and path are high cardinality.
  TM_FINDING_SEVERITY: 'tm.finding.severity',
  TM_FINDING_CATEGORY: 'tm.finding.category',
  TM_FINDING_RULE_ID: 'tm.finding.rule_id',
  TM_FINDING_FILE_PATH: 'tm.finding.file_path',
  TM_BUILD_JOB_ID: 'tm.build.job_id',
  TM_BUILD_STEP: 'tm.build.step',
  TM_BUILD_PATCH_MODE: 'tm.build.patch_mode',
  /** The upstream base version a patched build starts from. High cardinality. */
  TM_BUILD_BASE_VERSION: 'tm.build.base_version',
  /** `PatchStage` / patch reason from the builder's termination contract. Closed at the source. */
  TM_BUILD_PATCH_STAGE: 'tm.build.patch_stage',
  TM_BUILD_PATCH_REASON: 'tm.build.patch_reason',
  // Per-platform compile dimensions. ~5 combinations, so safe as metric dimensions (§17).
  TM_BUILD_OS: 'tm.build.os',
  TM_BUILD_ARCH: 'tm.build.arch',
  TM_TOOL_NAME: 'tm.tool.name',
  TM_TOOL_RESULT: 'tm.tool.result',
  TM_TOOL_EXIT_CODE: 'tm.tool.exit_code',

  // tm.* - queue and workflow
  TM_QUEUE_NAME: 'tm.queue.name',
  TM_QUEUE_MSG_KEY: 'tm.queue.msg_key',
  TM_QUEUE_ATTEMPTS: 'tm.queue.attempts',
  TM_QUEUE_SETTLE: 'tm.queue.settle',
  TM_QUEUE_SETTLE_REASON: 'tm.queue.settle_reason',
  TM_WORKFLOW_NAME: 'tm.workflow.name',
  TM_WORKFLOW_PHASE: 'tm.workflow.phase',
  TM_WORKFLOW_STEP: 'tm.workflow.step',
  TM_WORKFLOW_ATTEMPT: 'tm.workflow.attempt',

  // tm.* - cron
  TM_CRON_TASK: 'tm.cron.task',

  // tm.* - serve
  TM_SERVE_DECISION: 'tm.serve.decision',
  TM_SERVE_DENY_REASON: 'tm.serve.deny_reason',

  // tm.* - registry client (parsed from user_agent.original; see TM_CLIENT_NAMES)
  TM_CLIENT_NAME: 'tm.client.name',
  /**
   * The client's semver, e.g. `1.9.5`. Absent (never empty) when `tm.client.name` is `other`.
   *
   * DELIBERATELY a metric dimension despite being tens of values: "which Terraform versions hit
   * this registry" is a dashboard question, and tens × route × status was accepted as the series
   * cost (decision 2026-10-07). It is NOT in FORBIDDEN_METRIC_DIMENSIONS for that reason - if the
   * series count ever bites, demote it here and on `recordHttpDuration` before touching the gateway.
   */
  TM_CLIENT_VERSION: 'tm.client.version',
} as const

export type AttrKey = (typeof ATTR)[keyof typeof ATTR]

/**
 * Keys that must never be a metric dimension (§15, §17).
 *
 * Enforced in THREE places on purpose: here for review, in the metric helpers that read this list,
 * and at the gateway, which deletes them from both the datapoint and the resource context. The
 * gateway is the one that actually protects the TSDB, because it cannot be bypassed by a mistake in
 * one service.
 */
export const FORBIDDEN_METRIC_DIMENSIONS = closed([
  ATTR.JOURNEY_ID,
  ATTR.TENANT_ID,
  ATTR.TENANT_SLUG,
  ATTR.USER_ID,
  ATTR.USER_EMAIL,
  ATTR.SESSION_ID,
  ATTR.DEVICE_ID,
  ATTR.TM_WORKSPACE_ID,
  ATTR.TM_WORKSPACE_NAME,
  ATTR.TM_ARTEFACT_VERSION_ID,
  ATTR.TM_SCAN_JOB_ID,
  ATTR.TM_BUILD_JOB_ID,
  ATTR.TM_PROVIDER_VERSION,
  ATTR.TM_MODULE_NAME,
  ATTR.TM_MODULE_VERSION,
  ATTR.TM_BUILD_BASE_VERSION,
  ATTR.TM_SCAN_RULE_ID,
  ATTR.TM_STATE_SERIAL,
  ATTR.TM_STATE_LINEAGE,
  ATTR.TM_FINDING_RULE_ID,
  ATTR.TM_FINDING_FILE_PATH,
  ATTR.TM_ACTOR_NAME,
  ATTR.TM_QUEUE_MSG_KEY,
  ATTR.TM_WORKFLOW_NAME,
  ATTR.URL_PATH,
  ATTR.URL_FULL,
  ATTR.TM_STORAGE_KEY,
])

/** True when `value` is a member of a closed set. Use before emitting an untrusted enum value. */
export function isMember<T extends readonly string[]>(set: T, value: unknown): value is T[number] {
  return typeof value === 'string' && (set as readonly string[]).includes(value)
}
